import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { LoaderIcon } from "@/components/icons";
import Case from "@/components/shared/case";
import { cn } from "@/lib/utils";

/**
 * Button Next-Elite (variantes completas), adaptado ao Creative OS (Vite).
 * Sem next-intl; loading usa rotulo fixo em PT.
 */
const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent text-sm font-semibold tracking-[-0.005em] transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:stroke-[1.75] shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-primary/35 aria-invalid:border-destructive aria-invalid:ring-destructive/20",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary-pressed",
        primary:
          "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary-pressed",
        ink: "bg-ink text-ink-foreground hover:bg-ink-hover",
        subtle: "bg-primary-soft text-foreground hover:bg-primary-soft/80",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/30",
        destructiveSubtle:
          "bg-danger-soft text-danger-soft-foreground hover:bg-danger-soft/80 focus-visible:ring-destructive/30",
        outline:
          "border-border bg-card text-foreground hover:border-border-strong hover:bg-sunken",
        outlineSuccess:
          "border-success-line border-dashed bg-transparent text-success-soft-foreground",
        outlineWarning:
          "border-warning-line border-dashed bg-transparent text-warning-soft-foreground",
        outlineDestructive:
          "border-danger-line border-dashed bg-transparent text-danger-soft-foreground",
        secondary:
          "border-border bg-card text-foreground hover:border-border-strong hover:bg-sunken",
        ghost:
          "text-foreground-secondary hover:bg-sunken hover:text-foreground",
        ghostPrimary: "text-foreground hover:bg-primary-soft",
        ghostDestructive:
          "text-danger-soft-foreground hover:bg-danger-soft",
        toggle: "text-foreground-secondary hover:bg-transparent hover:text-foreground",
        link: "h-auto px-0 text-foreground underline decoration-[1.5px] underline-offset-[3px] hover:decoration-primary",
        accent: "bg-primary-soft text-foreground hover:bg-primary-soft/80",
        muted: "bg-sunken text-muted-foreground",
        success:
          "bg-success text-success-foreground hover:bg-success/90 focus-visible:ring-success/30",
      },
      size: {
        default: "h-10 px-4 has-[>svg]:px-3.5",
        sm: "h-8 gap-1.5 rounded-sm px-3 text-[13px] has-[>svg]:px-2.5",
        xs: "h-7 gap-1 rounded-sm px-2.5 text-xs has-[>svg]:px-2",
        lg: "h-11 px-5 text-[15px] has-[>svg]:px-4",
        icon: "size-10",
        "icon-sm": "size-8 rounded-sm",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function ButtonComp({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

function Button({
  children,
  loading = false,
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const isDisabled = loading || props.disabled;

  if (asChild) {
    return (
      <ButtonComp
        variant={variant}
        size={size}
        className={className}
        asChild
        disabled={isDisabled}
        {...props}
      >
        {children}
      </ButtonComp>
    );
  }

  return (
    <ButtonComp
      variant={variant}
      size={size}
      className={className}
      disabled={isDisabled}
      {...props}
    >
      <Case condition={loading}>
        <LoaderIcon />
        Carregando…
      </Case>
      <Case condition={!loading}>{children}</Case>
    </ButtonComp>
  );
}

export { Button, buttonVariants };
